/**
 * TrackingRollup.jsx - a main project's Tracking roll-up (10/14/26): the
 * headline Summary numbers of every record under it added up, and one line
 * per record, in the Finance or the Project-management lens. Each record's
 * own tracking is unchanged (its Tracking tab); this only reads them
 * (GET /api/project-costs/:id per record, as the tab does) and adds up the
 * engine's figures - nothing is priced again here. Cost, profit and margin
 * show only when the server sent them ('cost-rates').
 */
import { useEffect, useState } from 'react';
import { getProjectCosts, hours, money, pct } from '../../costs/costApi';
import { isoOf, progressOf } from '../../projectCosts/trackingInsights';
import { Segmented, Stat } from '../../projectCosts/trackingUi';
import { MUTED } from '../../quotes/quoteUi';

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** One record's headline figures from its tracking answer. PURE. */
export function trackingFigures(data, today) {
  const t = data && data.tracking;
  if (!t) return null;
  const P = progressOf(data, today);
  const S = t.asSold || null;
  return {
    soldNow: n(t.totals && t.totals.soldTotal),
    cost: S ? n(S.cost) : null,
    profit: S ? n(S.profit) : null,
    poValue: n(t.invoicing && t.invoicing.poValue),
    invoiced: n(t.invoicing && t.invoicing.invoiced),
    usedHours: n(P.usedHours),
    budgetHours: n(P.budgetHours),
    budgetSold: n(P.budgetSold),
  };
}

/** The figures added up over the records; margin and the used shares worked out from the sums. PURE. */
export function sumFigures(list) {
  const rows = (list || []).filter(Boolean);
  const add = (k) => rows.reduce((a, r) => a + n(r[k]), 0);
  const costs = rows.length > 0 && rows.every((r) => r.cost !== null);
  const out = {
    soldNow: add('soldNow'),
    poValue: add('poValue'),
    invoiced: add('invoiced'),
    usedHours: add('usedHours'),
    budgetHours: add('budgetHours'),
    budgetSold: add('budgetSold'),
    cost: costs ? add('cost') : null,
    profit: costs ? add('profit') : null,
  };
  out.margin = costs && out.soldNow ? out.profit / out.soldNow : null;
  out.hoursPct = out.budgetHours > 0 ? out.usedHours / out.budgetHours : null;
  out.soldPct = out.budgetSold > 0 ? out.soldNow / out.budgetSold : null;
  return out;
}

const th = {
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 800,
  color: MUTED,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  padding: '6px 8px',
  borderBottom: '1px solid #E5E9DD',
  whiteSpace: 'nowrap',
};
const td = { fontSize: 12, padding: '6px 8px', borderBottom: '1px solid #F0F2EC', fontVariantNumeric: 'tabular-nums' };
const num = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

export default function TrackingRollup({ records, onOpen, today: todayProp }) {
  const today = todayProp || isoOf(new Date());
  const [state, setState] = useState({ phase: 'loading', rows: [] });
  const [lens, setLens] = useState('finance');
  const ids = (records || []).map((r) => String(r.id)).join('|');
  useEffect(() => {
    let live = true;
    const list = records || [];
    setState({ phase: 'loading', rows: [] });
    Promise.all(list.map((r) => getProjectCosts(r.id).then((res) => ({ record: r, ok: res.ok, data: res.body })))).then((rows) => {
      if (live) setState({ phase: 'ready', rows });
    });
    return () => {
      live = false;
    };
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.phase === 'loading')
    return <div style={{ fontSize: 12, color: MUTED }}>Adding up the tracking of {records.length} records...</div>;
  const rows = state.rows.map((r) => ({ ...r, fig: r.ok ? trackingFigures(r.data, today) : null }));
  const T = sumFigures(rows.map((r) => r.fig));
  const costs = T.cost !== null;
  const nameOf = (r) => [r.globalProjectNumber || r.global_project_number, r.name || r.description].filter(Boolean).join(' · ');
  const po = (r) => r.poNumber || r.po_number || '';

  return (
    <div data-testid="tracking-rollup">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <Segmented
          label="Roll-up lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'finance', label: 'Finance' },
            { value: 'pm', label: 'Project management' },
          ]}
        />
        <span style={{ fontSize: 11.5, color: MUTED }}>
          The Summary of every record under this main project, added up. Each record's own tracking is on its Tracking tab.
        </span>
      </div>
      {lens === 'finance' ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <Stat big label="As sold" value={money(T.soldNow)} note="What the work done so far is worth, all records." />
          {costs && <Stat big label="As sold cost" value={money(T.cost)} />}
          {costs && <Stat big label="Gross profit" value={money(T.profit)} accent={T.profit < 0 ? '#B71C1C' : undefined} />}
          {costs && <Stat big label="Margin" value={T.margin === null ? '-' : pct(T.margin)} />}
          <Stat
            big
            label="PO lines"
            value={money(T.poValue)}
            note={`${money(T.invoiced)} invoiced, ${money(T.poValue - T.invoiced)} open.`}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <Stat
            big
            label="Hours used"
            value={T.hoursPct === null ? hours(T.usedHours) : pct(T.hoursPct)}
            note={
              T.budgetHours
                ? `${hours(T.usedHours)} of ${hours(T.budgetHours)} budget hours.`
                : `${hours(T.usedHours)} hours logged - no hours budget.`
            }
          />
          <Stat
            big
            label="Budget used"
            value={T.soldPct === null ? '-' : pct(T.soldPct)}
            note={T.budgetSold ? `${money(T.soldNow)} As sold of ${money(T.budgetSold)}.` : 'No budget yet.'}
          />
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Tracking by record">
          <thead>
            <tr>
              <th style={th}>Record</th>
              <th style={th}>PO #</th>
              {lens === 'finance' ? (
                <>
                  <th style={{ ...th, textAlign: 'right' }}>As sold</th>
                  {costs && <th style={{ ...th, textAlign: 'right' }}>Cost</th>}
                  {costs && <th style={{ ...th, textAlign: 'right' }}>Profit</th>}
                  <th style={{ ...th, textAlign: 'right' }}>PO lines</th>
                  <th style={{ ...th, textAlign: 'right' }}>Invoiced</th>
                </>
              ) : (
                <>
                  <th style={{ ...th, textAlign: 'right' }}>Hours</th>
                  <th style={{ ...th, textAlign: 'right' }}>Budget hrs</th>
                  <th style={{ ...th, textAlign: 'right' }}>As sold</th>
                  <th style={{ ...th, textAlign: 'right' }}>Budget</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ record: r, fig }) => (
              <tr key={r.id} onClick={() => onOpen && onOpen(r.id)} style={{ cursor: onOpen ? 'pointer' : undefined }}>
                <td style={td}>{nameOf(r) || r.id}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{po(r) || '-'}</td>
                {!fig ? (
                  <td style={{ ...td, color: MUTED }} colSpan={lens === 'finance' ? (costs ? 5 : 3) : 4}>
                    No tracking read for this record.
                  </td>
                ) : lens === 'finance' ? (
                  <>
                    <td style={num}>{money(fig.soldNow)}</td>
                    {costs && <td style={num}>{money(fig.cost)}</td>}
                    {costs && <td style={num}>{money(fig.profit)}</td>}
                    <td style={num}>{money(fig.poValue)}</td>
                    <td style={num}>{money(fig.invoiced)}</td>
                  </>
                ) : (
                  <>
                    <td style={num}>{hours(fig.usedHours)}</td>
                    <td style={num}>{fig.budgetHours ? hours(fig.budgetHours) : '-'}</td>
                    <td style={num}>{money(fig.soldNow)}</td>
                    <td style={num}>{fig.budgetSold ? money(fig.budgetSold) : '-'}</td>
                  </>
                )}
              </tr>
            ))}
            <tr style={{ background: '#F6F8F3', fontWeight: 800 }}>
              <td style={{ ...td, fontWeight: 800 }} colSpan={2}>
                All {rows.length} records
              </td>
              {lens === 'finance' ? (
                <>
                  <td style={num}>{money(T.soldNow)}</td>
                  {costs && <td style={num}>{money(T.cost)}</td>}
                  {costs && <td style={num}>{money(T.profit)}</td>}
                  <td style={num}>{money(T.poValue)}</td>
                  <td style={num}>{money(T.invoiced)}</td>
                </>
              ) : (
                <>
                  <td style={num}>{hours(T.usedHours)}</td>
                  <td style={num}>{T.budgetHours ? hours(T.budgetHours) : '-'}</td>
                  <td style={num}>{money(T.soldNow)}</td>
                  <td style={num}>{T.budgetSold ? money(T.budgetSold) : '-'}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
